import { useRef, type ReactNode } from 'react';

interface FileTriggerBaseProps {
  /** `accept` passed to the native picker, e.g. "image/jpeg,image/png". */
  accept: string;
  /** Render prop — call `open()` from any trigger UI (Button, NavRow…). */
  children: (open: () => void) => ReactNode;
}

export type FileTriggerProps = FileTriggerBaseProps &
  (
    | { multiple?: false; onSelect: (file: File) => void }
    | { multiple: true; onSelect: (files: File[]) => void }
  );

/**
 * FileTrigger — owns a visually-hidden native file input and hands the
 * trigger UI an `open()` callback. Keeps raw `<input type="file">` inside
 * @fluent/ui so feature code needs no lint escapes. The input value resets
 * after every pick so re-selecting the same file fires onSelect again.
 *
 * With `multiple`, onSelect receives every picked file as an array.
 */
export function FileTrigger(props: FileTriggerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      {props.children(() => inputRef.current?.click())}
      <input
        ref={inputRef}
        type="file"
        accept={props.accept}
        multiple={props.multiple === true}
        style={{ display: 'none' }}
        onChange={(e) => {
          const list = e.target.files;
          if (list && list.length > 0) {
            if (props.multiple === true) props.onSelect(Array.from(list));
            else props.onSelect(list[0]!);
          }
          e.target.value = '';
        }}
      />
    </>
  );
}
